import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import type { FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';

import { isNil } from 'lodash';

/** Option for enum multi-select: value (sent to backend) and name (display label) */
export interface EnumValueOption<T extends string = string> {
  value: T;
  name: string;
}

/** Item emitted by crm-multi-select when options are objects */
type SelectableItem<T extends string = string> = EnumValueOption<T> | T;

/**
 * Reusable multi-select for enums with localized labels.
 * Always works with values (string[]) for two-way binding — never emits objects.
 * Displays chips with localized names and allows removing items one by one.
 *
 * @template T - enum value type (default: string)
 */
@Component({
  selector: 'crm-enum-values-multi-select',
  standalone: true,
  imports: [CommonModule, MultiSelectComponent],
  templateUrl: './enum-values-multi-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EnumValuesMultiSelectComponent<T extends string = string> {
  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';

  @Input() public options: EnumValueOption<T>[] = [];
  @Input() public selectedValues: T[] = [];
  @Output() public selectedValuesChange = new EventEmitter<T[]>();

  @Input() public placeholder = '\u00A0';
  @Input() public class = '';
  @Input() public styleClass = '';
  @Input() public isError = false;
  @Input() public appendTo: string | undefined;
  @Input() public disabled = false;
  @Input() public showClear = true;

  public constructor() {}

  public onSelectedChange(items: SelectableItem<T>[]): void {
    const values = items.map((item: SelectableItem<T>): T | undefined => {
      if (typeof item === 'string') {
        return item;
      }
      if (isNil(item) || typeof item !== 'object' || 'value' in item === false) {
        return undefined;
      }
      return (item as EnumValueOption<T>).value;
    });
    this.selectedValuesChange.emit(values.filter((value): value is T => !isNil(value)));
  }
}
